import { useEffect, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { Search } from 'lucide-react'
import type { SearchResult } from '@shared/types'
import { api } from '../../lib/api'
import { useStore } from '../../store'
import { cn } from '../../lib/utils'

/**
 * Command-palette symbol search. Press ⌘K / Ctrl+K anywhere to open, type a ticker or
 * company name → live EODHD results → pick one (or Enter on raw text) and the full symbol
 * detail modal opens. Self-contained: owns its open state + the global hotkey.
 */
export function SymbolPalette() {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [results, setResults] = useState<SearchResult[] | null>(null)
  const [active, setActive] = useState(0)
  const [loading, setLoading] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const openSymbol = useStore((s) => s.openSymbol)

  // Global hotkey: ⌘K / Ctrl+K toggles the palette (works from anywhere, even inputs).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault()
        setOpen((o) => !o)
      } else if (e.key === 'Escape') {
        setOpen(false)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [])

  // Reset + focus each time it opens.
  useEffect(() => {
    if (!open) return
    setQ('')
    setResults(null)
    setActive(0)
    const t = setTimeout(() => inputRef.current?.focus(), 30)
    return () => clearTimeout(t)
  }, [open])

  // Debounced live search.
  useEffect(() => {
    if (!open) return
    const term = q.trim()
    if (!term) {
      setResults(null)
      setLoading(false)
      return
    }
    let alive = true
    setLoading(true)
    const t = setTimeout(() => {
      api
        .search(term)
        .then((r) => {
          if (!alive) return
          setResults(r.slice(0, 10))
          setActive(0)
          setLoading(false)
        })
        .catch(() => {
          if (!alive) return
          setResults([])
          setLoading(false)
        })
    }, 200)
    return () => {
      alive = false
      clearTimeout(t)
    }
  }, [q, open])

  const choose = (symbol: string) => {
    const s = symbol.trim().toUpperCase()
    if (!s) return
    setOpen(false)
    openSymbol(s) // opens the existing full SymbolModal
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[70] flex items-start justify-center p-4 pt-[12vh]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <div className="absolute inset-0 bg-black/55" onClick={() => setOpen(false)} />
          <motion.div
            initial={{ y: 12, scale: 0.98, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 12, scale: 0.98, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 340, damping: 30 }}
            className="glass relative w-full max-w-lg overflow-hidden"
            style={{ borderRadius: 'var(--radius-lg)' }}
          >
            <div className="flex items-center gap-2.5 border-b border-[var(--line-soft)] px-4 py-3">
              <Search size={17} className="shrink-0 text-fg-subtle" />
              <input
                ref={inputRef}
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown') {
                    e.preventDefault()
                    setActive((a) => Math.min(a + 1, (results?.length ?? 1) - 1))
                  } else if (e.key === 'ArrowUp') {
                    e.preventDefault()
                    setActive((a) => Math.max(a - 1, 0))
                  } else if (e.key === 'Enter') {
                    e.preventDefault()
                    if (results && results[active]) choose(results[active].symbol)
                    else choose(q)
                  }
                }}
                placeholder="Search any symbol or company — NVDA, Micron, $^VIX…"
                className="min-w-0 flex-1 bg-transparent text-[15px] text-fg placeholder:text-fg-faint focus:outline-none"
              />
              <kbd className="hidden shrink-0 rounded border border-[var(--line-soft)] px-1.5 py-0.5 text-[10px] text-fg-faint sm:block">ESC</kbd>
            </div>
            <div className="max-h-[50vh] overflow-y-auto p-1.5">
              {results && results.length > 0 ? (
                results.map((r, i) => (
                  <button
                    key={`${r.symbol}-${i}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(r.symbol)}
                    className={cn(
                      'flex w-full items-center justify-between gap-3 rounded-lg px-3 py-2 text-left transition-colors',
                      i === active ? 'bg-[var(--color-surface-2)]' : '',
                    )}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-[13px] font-medium text-fg">{r.name}</span>
                      <span className="block text-[11px] text-fg-subtle">
                        {r.type} · {r.exchange}
                        {r.country ? ` · ${r.country}` : ''}
                      </span>
                    </span>
                    <span className="num shrink-0 rounded bg-[var(--color-inset)] px-1.5 py-0.5 text-[11.5px] font-semibold text-fg">
                      {r.code}
                    </span>
                  </button>
                ))
              ) : (
                <div className="px-3 py-6 text-center text-[12.5px] text-fg-faint">
                  {loading ? 'Searching…' : q.trim() ? 'No matches — press Enter to open as-is' : 'Type a ticker or company name'}
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  )
}
